import { cn } from 'src/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import type { EtlDailyRollupDay } from '../types/operations.types';
import {
  formatRollupTooltip,
  ROLLUP_BAR_COLOURS,
  ROLLUP_STATUS_LABELS,
} from '../helpers/etl-health.helpers';

type Props = {
  days: EtlDailyRollupDay[];
  selectedDate: string | null;
  onSelectDate: (date: string | null) => void;
};

export function EtlHealthStrip({ days, selectedDate, onSelectDate }: Props) {
  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-4 text-xs text-gray-600">
          {(['green', 'yellow', 'red', 'no_data'] as const).map((status) => (
            <span key={status} className="inline-flex items-center gap-1.5">
              <span
                className={cn('inline-block size-2.5 rounded-sm', ROLLUP_BAR_COLOURS[status].split(' ')[0])}
                aria-hidden="true"
              />
              {ROLLUP_STATUS_LABELS[status]}
            </span>
          ))}
        </div>

        <div
          className="flex h-16 items-end gap-1 rounded-lg border border-gray-200 bg-gray-50 p-3"
          role="list"
          aria-label="30-day ETL health strip"
        >
          {days.map((day) => {
            const isSelected = selectedDate === day.date;
            return (
              <Tooltip key={day.date}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    role="listitem"
                    aria-label={formatRollupTooltip(day)}
                    aria-pressed={isSelected}
                    onClick={() => onSelectDate(isSelected ? null : day.date)}
                    className={cn(
                      'min-w-0 flex-1 rounded-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500',
                      ROLLUP_BAR_COLOURS[day.overall_status],
                      isSelected ? 'h-12 ring-2 ring-violet-600 ring-offset-1' : 'h-8',
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{formatRollupTooltip(day)}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        <div className="flex justify-between text-xs text-gray-500">
          <span>{days[0]?.date ?? ''}</span>
          <span>{days[days.length - 1]?.date ?? ''}</span>
        </div>
      </div>
    </TooltipProvider>
  );
}
